import type { AdminProfile, ProfileRole } from "@/api/model";
import { ROLE_LABELS } from "./constants/roles";

/**
 * Get user initials from name
 */
export function getUserInitials(user: AdminProfile): string {
  if (!user.name) return "??";
  const parts = user.name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return user.name.substring(0, 2).toUpperCase();
}

/**
 * Get user roles as display strings.
 *
 * The Role column shows Player / Parent / Staff and nothing else, per the Users
 * Refresh design: roles without a pill of their own render no pill rather than a
 * capitalised enum value. ADMIN is the shield icon next to the name, INSTRUCTOR
 * reads as a confusing second kind of staff outside the booking context, and a
 * payer-only contact is too easily misread as "Player".
 */
export function getUserRoles(user: AdminProfile): string[] {
  const roles: string[] = [];

  if (user.is_coach) roles.push("Staff");
  if (user.is_tfa_employee) roles.push("TFA Employee");

  // Add roles from the roles array if available
  if (user.roles) {
    user.roles.forEach((role: ProfileRole) => {
      const roleLabel = getRoleColumnLabel(role, user);
      if (roleLabel && !roles.includes(roleLabel)) {
        roles.push(roleLabel);
      }
    });
  }

  return roles;
}

/**
 * The pill a role earns in the Role column, or null for one that shows none.
 *
 * The CONTACT role's label derives from the user's typed relationship links:
 * has_parent_link is true for any parent-typed link — and for link-less
 * contacts (parent-first creation), so both read "Parent". Non-parent links get
 * no pill; the relationship is visible in the Relationships count and tab
 * instead. Older payloads without the flag default to "Parent".
 */
function getRoleColumnLabel(
  role: ProfileRole,
  user: AdminProfile
): string | null {
  switch (role.toLowerCase()) {
    case "player":
      return ROLE_LABELS.player;
    case "coach":
      return ROLE_LABELS.coach;
    case "contact":
      return user.has_parent_link === false ? null : ROLE_LABELS.contact;
    default:
      return null;
  }
}

/**
 * Tag props for a role pill, per the Users Refresh design: blue Player,
 * purple Parent, green Staff; anything else neutral. Purple has no PrimeVue
 * severity, so Parent overrides the secondary severity's surface colors.
 */
export function getRoleTagProps(role: string): {
  severity: "info" | "success" | "secondary";
  class?: string;
} {
  switch (role) {
    case "Player":
      return { severity: "info" };
    case "Staff":
      return { severity: "success" };
    case "Parent":
      return {
        severity: "secondary",
        class:
          "!text-purple-700 dark:!text-purple-300 !bg-purple-100 dark:!bg-purple-500/20",
      };
    default:
      return { severity: "secondary" };
  }
}

/**
 * Get team name from user
 * For multiple teams, returns "Team 1 and X more"
 */
export function getTeamName(user: AdminProfile): string {
  if (user.teams && user.teams.length > 0) {
    if (user.teams.length === 1) {
      return user.teams[0].name || "No Team";
    }
    const firstName = user.teams[0].name || "Team";
    const remaining = user.teams.length - 1;
    return `${firstName} and ${remaining} more`;
  }
  return "No Team";
}

/**
 * Get all team names as comma-separated string
 */
export function getAllTeamNames(user: AdminProfile): string {
  if (user.teams && user.teams.length > 0) {
    return user.teams.map((team) => team.name || "Unknown").join(", ");
  }
  return "No Team";
}

/**
 * Get app status display
 */
export function getAppStatus(user: AdminProfile): {
  label: string;
  severity: "success" | "info" | "warn" | "secondary";
} {
  const status = user.mobile_app_status;

  if (status === "logged in") {
    return { label: "Active", severity: "success" };
  } else if (status === "invited") {
    return { label: "Invited", severity: "info" };
  }
  return { label: "Not Invited", severity: "secondary" };
}

/**
 * Format the invited date for display
 */
export function getInvitedDate(invitedAt: string | null): string | null {
  if (!invitedAt) return null;

  try {
    const date = new Date(invitedAt);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

/** One line for the user page: where texting consent stands for this profile's number. */
export function smsConsentLabel(user: {
  phone_number?: string | null;
  sms_consent_at?: string | null;
  sms_opted_out_at?: string | null;
  sms_consent_source?: string | null;
}): { text: string; optedOut: boolean } {
  if (!user.phone_number) return { text: "No phone number", optedOut: false };
  if (user.sms_opted_out_at) {
    return {
      text: `Opted out ${formatDay(user.sms_opted_out_at)} (replied STOP)`,
      optedOut: true,
    };
  }
  if (user.sms_consent_at) {
    const via = SMS_CONSENT_SOURCES[user.sms_consent_source ?? ""] ?? "";
    return {
      text: `Opted in ${formatDay(user.sms_consent_at)}${via ? ` via ${via}` : ""}`,
      optedOut: false,
    };
  }
  return { text: "No consent on file", optedOut: false };
}

const SMS_CONSENT_SOURCES: Record<string, string> = {
  portal_account: "account signup",
  twilio_start: "texting START",
  admin_written: "written consent",
};

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
